import { useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { DroneQuad, type QuadDrive } from "./DroneQuad";
import { BAYS, STATUS_LABEL, type Bay } from "./bays";

/**
 * The menu, Free's design: one racing quad on a launch stand per bay, lined
 * up on the deck facing the user. Dark until the hero settles, then the
 * lights come up. Hover a bay: its quad spools, lifts, tilts 45 degrees at
 * you, lens awake. Click: it launches at the camera and the lens-wipe
 * overlay (HomePage) grows into the destination.
 */

export type LaunchRequest = { bay: Bay; x: number; y: number }; // x,y in vw/vh %

const STAND_TOP = 1.04;

export function MenuDrones({
  settled,
  launchingId,
  onLaunch,
}: {
  settled: boolean;
  launchingId: string | null;
  onLaunch: (req: LaunchRequest) => void;
}) {
  const { camera, size } = useThree();
  const [hovered, setHovered] = useState<string | null>(null);

  // captured once, like the hero: the flight line does not re-rig on resize
  const aspect0 = useRef(size.width / Math.max(1, size.height)).current;
  const narrow = aspect0 < 1.05;
  const spacing = narrow ? 1.55 : 2.6;
  const quadScale = narrow ? 0.8 : 1.15;
  const standX = (i: number) => (i - (BAYS.length - 1) / 2) * spacing;
  const standZ = (i: number) => 1.5 - Math.abs(i - (BAYS.length - 1) / 2) * 0.24;

  const drives = useMemo(() => {
    const m = new Map<string, QuadDrive>();
    for (const b of BAYS) m.set(b.id, { hover: false, launching: false });
    return m;
  }, []);
  for (const b of BAYS) {
    const d = drives.get(b.id)!;
    d.hover = hovered === b.id && settled;
    d.launching = launchingId === b.id;
  }

  // menu illumination: everything sits dark until the wordmark settles
  const glow = useMemo(() => ({ k: 0 }), []);
  const ambRef = useRef<THREE.AmbientLight>(null);
  const dirRef = useRef<THREE.DirectionalLight>(null);
  useFrame((_, dt) => {
    glow.k += ((settled ? 1 : 0) - glow.k) * Math.min(1, dt * 1.6);
    if (ambRef.current) ambRef.current.intensity = 0.06 + glow.k * 0.5;
    if (dirRef.current) dirRef.current.intensity = 0.04 + glow.k * 1.25;
  });

  const standGeo = useMemo(() => new THREE.CylinderGeometry(0.15, 0.3, 1.0, 6), []);
  const plateGeo = useMemo(() => new THREE.BoxGeometry(0.46, 0.05, 0.46), []);
  const standMat = useMemo(
    () => new THREE.MeshStandardMaterial({ color: "#a41e1e", roughness: 0.5, metalness: 0.25 }),
    []
  );

  const v = useMemo(() => new THREE.Vector3(), []);
  const launch = (bay: Bay, i: number) => {
    if (!settled || launchingId) return;
    v.set(standX(i), STAND_TOP + 0.35, standZ(i)).project(camera);
    onLaunch({ bay, x: ((v.x + 1) / 2) * 100, y: ((1 - v.y) / 2) * 100 });
  };
  const hoverOn = (id: string) => {
    if (!settled) return;
    setHovered(id);
    document.body.style.cursor = "pointer";
  };
  const hoverOff = () => {
    setHovered(null);
    document.body.style.cursor = "";
  };

  return (
    <group>
      <ambientLight ref={ambRef} intensity={0.06} />
      <directionalLight ref={dirRef} position={[4, 9, 7]} intensity={0.04} />

      {BAYS.map((b, i) => (
        <group key={b.id} position={[standX(i), 0, standZ(i)]}>
          {/* the MultiGP-red launch stand */}
          <mesh geometry={standGeo} material={standMat} position={[0, 0.5, 0]} />
          <mesh geometry={plateGeo} material={standMat} position={[0, 1.02, 0]} />

          {/* hover/click catcher around stand + quad (bigger than the meshes) */}
          <mesh
            position={[0, 0.9, 0.1]}
            onPointerOver={(e) => {
              e.stopPropagation();
              hoverOn(b.id);
            }}
            onPointerOut={hoverOff}
            onClick={(e) => {
              e.stopPropagation();
              launch(b, i);
            }}
            visible={false}
          >
            <boxGeometry args={[1.6, 2.4, 1.6]} />
          </mesh>

          {settled && (
            <Html center position={[0, -0.42, 0.4]} zIndexRange={[20, 0]} wrapperClass="bay3-wrap">
              <div
                className={"bay3" + (hovered === b.id ? " hov" : "")}
                onPointerEnter={() => hoverOn(b.id)}
                onPointerLeave={hoverOff}
                onClick={() => launch(b, i)}
              >
                <span className="status">
                  <span className={"dot " + b.status} />
                  {STATUS_LABEL[b.status]}
                </span>
                <span className="bay3-label">{b.label}</span>
                <span className="bay3-tag">{b.tagline}</span>
              </div>
            </Html>
          )}
        </group>
      ))}

      {BAYS.map((b, i) => (
        <DroneQuad
          key={b.id}
          position={[standX(i), STAND_TOP + 0.06, standZ(i)]}
          accent={b.accent}
          drive={drives.get(b.id)!}
          glow={glow}
          scale={quadScale}
        />
      ))}
    </group>
  );
}
